// The Plan tab's dates and edits (the meal planner — "plan" alone already means
// the billing plan and the desktop layout here, so the code says "meal plan").
// Ported from the approved prototype (feature pack reference/src/plan.ts).
// Days are local calendar dates keyed "YYYY-MM-DD"; weeks start on Monday and
// are numbered the ISO way.

import type { PlannedMeal } from './supabase'

export type Meal = Pick<PlannedMeal, 'id' | 'recipe_id' | 'servings' | 'added' | 'cooked'>
/** Meals by day key, in the order they were added. */
export type Plan = Record<string, Meal[]>

export const MEAL_SERVINGS_MIN = 1
export const MEAL_SERVINGS_MAX = 12

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const day0 = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
export const addDays = (d: Date, n: number) => { const x = new Date(d.getTime()); x.setDate(x.getDate() + n); return x }
export const mondayOf = (d: Date) => { const x = day0(d); return addDays(x, -((x.getDay() + 6) % 7)) }
export const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
export const fromKey = (k: string) => { const [y, m, d] = k.split('-').map(Number); return new Date(y, m - 1, d) }
/** The seven day keys of the week `offset` weeks from the one containing `today`. */
export const weekKeys = (today: Date, offset: number) => {
  const mon = addDays(mondayOf(today), offset * 7)
  return [0, 1, 2, 3, 4, 5, 6].map(i => dayKey(addDays(mon, i)))
}

export function weekNumber(d: Date): number {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const day = x.getUTCDay() || 7
  x.setUTCDate(x.getUTCDate() + 4 - day)
  const y0 = new Date(Date.UTC(x.getUTCFullYear(), 0, 1))
  return Math.ceil(((x.getTime() - y0.getTime()) / 86400000 + 1) / 7)
}

export const weekdayShort = (k: string) => DOW[(fromKey(k).getDay() + 6) % 7]
export const dayLabel = (k: string) => `${weekdayShort(k)} ${fromKey(k).getDate()}`
export const dateLabel = (k: string) => { const d = fromKey(k); return `${d.getDate()} ${MON[d.getMonth()]}` }

/** "5 Oct to 11 Oct" — a week's span under its title. */
export const weekSpan = (keys: string[]) => (keys.length ? `${dateLabel(keys[0])} to ${dateLabel(keys[keys.length - 1])}` : '')

/** "Wed 7 Oct", "Wed 7 to Fri 9 Oct", or "Wed 7, Fri 9" when there are gaps. */
export function rangeLabel(keys: string[]): string {
  if (!keys.length) return ''
  const s = [...keys].sort()
  const last = fromKey(s[s.length - 1])
  const contiguous = s.every((k, i) => i === 0 || dayKey(addDays(fromKey(s[i - 1]), 1)) === k)
  if (s.length === 1) return `${dayLabel(s[0])} ${MON[last.getMonth()]}`
  if (contiguous) return `${dayLabel(s[0])} to ${dayLabel(s[s.length - 1])} ${MON[last.getMonth()]}`
  return s.map(dayLabel).join(', ')
}

export const clampServings = (n: number) => Math.min(MEAL_SERVINGS_MAX, Math.max(MEAL_SERVINGS_MIN, Math.round(n)))

/** Rows as stored → meals by day, oldest first within a day. */
export function planFromRows(rows: PlannedMeal[]): Plan {
  const plan: Plan = {}
  for (const r of [...rows].sort((a, b) => a.created_at.localeCompare(b.created_at))) {
    ;(plan[r.day] ??= []).push({ id: r.id, recipe_id: r.recipe_id, servings: r.servings, added: r.added, cooked: r.cooked })
  }
  return plan
}

/** The meals of the ticked days, in date order. */
export const mealsForDays = (p: Plan, ticked: Record<string, boolean>) =>
  Object.keys(ticked).filter(k => ticked[k]).sort().flatMap(k => p[k] ?? [])

/** Today through Sunday are ticked when the Plan tab opens. */
export function defaultSelection(today: Date): Record<string, boolean> {
  const sel: Record<string, boolean> = {}
  const sun = addDays(mondayOf(today), 6)
  for (let d = day0(today); d <= sun; d = addDays(d, 1)) sel[dayKey(d)] = true
  return sel
}
